// DepartmentDropdownWithContext.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { TreeNode } from 'types/interfaces/TreeNode';

import { DepartmentsService } from 'components/services';
import { GlobalSettings } from 'components/services/GlobalSettingsService';
import { CustomDropdown } from 'ui/custom-controls';

import {
  isPlanningAlgorithmError,
  PLANNING_ALGORITHM_ERROR_MESSAGE
} from '../../utils/taskCard/taskAssignmentErrors';

import { useDepartmentContext } from './DepartmentContext';

interface DepartmentDropdownWithContextProps {
  taskId: number;
  currentDepartment: TreeNode | null;
  hasAssetOrTaskType: boolean;
  assetId?: number;
  taskTypeId?: number;
  plannedEndDate?: string;
  plannedStartDate?: string;
  onSelect: (taskId: number, department: TreeNode | null) => void;
  globalSettings: GlobalSettings;
}

export const DepartmentDropdownWithContext: React.FC<
  DepartmentDropdownWithContextProps
> = ({
  taskId,
  currentDepartment,
  hasAssetOrTaskType,
  assetId,
  taskTypeId,
  plannedEndDate,
  plannedStartDate,
  onSelect,
  globalSettings
}) => {
  const { showAllDepartments, toggleShowAllDepartments, departments } =
    useDepartmentContext();

  const [dropdownOpened, setDropdownOpened] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cachedDepartments, setCachedDepartments] = useState<TreeNode[] | null>(
    null
  );
  const [selectedValue, setSelectedValue] = useState<TreeNode | null>(
    currentDepartment
  );
  const [departmentPickerError, setDepartmentPickerError] = useState<
    string | null
  >(null);

  useEffect(() => {
    setSelectedValue(currentDepartment);
  }, [currentDepartment]);

  const getDisplayDepartments = useCallback(() => {
    if (showAllDepartments) {
      return departments;
    }
    return cachedDepartments || departments;
  }, [showAllDepartments, departments, cachedDepartments]);

  const taskDepartments = getDisplayDepartments();

  const loadDepartments = useCallback(async () => {
    if (showAllDepartments) return;
    if (!hasAssetOrTaskType) return;
    if (cachedDepartments) return;
    if (isLoading) return;

    const isPlanDatesEnabled =
      globalSettings?.plan_dates_department_check ?? true;
    const isTaskTypeEnabled = globalSettings?.taskType_department_check ?? true;

    setIsLoading(true);
    setDepartmentPickerError(null);
    try {
      const params: {
        assetId?: number;
        taskTypeId?: number;
        planStart?: string;
        planFinish?: string;
      } = {};

      if (assetId && assetId !== 0) {
        params.assetId = assetId;
      }

      if (isTaskTypeEnabled && taskTypeId && taskTypeId !== 0) {
        params.taskTypeId = taskTypeId;
      }

      if (isPlanDatesEnabled) {
        if (plannedStartDate) {
          params.planStart = plannedStartDate;
        }
        if (plannedEndDate) {
          params.planFinish = plannedEndDate;
        }
      }

      const response = await DepartmentsService.getDepartments(params);
      if (isPlanningAlgorithmError((response as any).error ?? response.data)) {
        setCachedDepartments([]);
        setDepartmentPickerError(PLANNING_ALGORITHM_ERROR_MESSAGE);
        return;
      }
      const departmentsData = response.data as unknown as TreeNode[];
      if (departmentsData && Array.isArray(departmentsData)) {
        setCachedDepartments(departmentsData);
      }
    } catch (error) {
      console.error('Error loading departments:', error);
      setDepartmentPickerError(null);
    } finally {
      setIsLoading(false);
    }
  }, [
    hasAssetOrTaskType,
    assetId,
    taskTypeId,
    plannedStartDate,
    plannedEndDate,
    cachedDepartments,
    isLoading,
    showAllDepartments,
    globalSettings
  ]);

  const handleClick = () => {
    if (!dropdownOpened && hasAssetOrTaskType) {
      setDropdownOpened(true);
      if (!showAllDepartments) {
        loadDepartments();
      }
    }
  };

  const handleTreeSelectionChange = useCallback(
    (selected: any) => {
      let selectedNode: TreeNode | null = null;

      if (selected && selected.key) {
        const findNodeByKey = (
          nodes: TreeNode[],
          key: string
        ): TreeNode | null => {
          for (const node of nodes) {
            if (node.key === key) return node;
            if (node.children) {
              const found = findNodeByKey(node.children, key);
              if (found) return found;
            }
          }
          return null;
        };

        const currentTaskDepartments = getDisplayDepartments();
        selectedNode = findNodeByKey(currentTaskDepartments, selected.key);
      }

      setSelectedValue(selectedNode);
      onSelect(taskId, selectedNode);
    },
    [taskId, onSelect, getDisplayDepartments]
  );

  const departmentsToShow = useMemo(() => {
    return taskDepartments && taskDepartments.length > 0 ? taskDepartments : [];
  }, [taskDepartments]);

  return (
    <div onClick={handleClick}>
      <CustomDropdown
        key={`department-${taskId}`}
        assetOptions={[]}
        treeValue={departmentsToShow}
        useTree={true}
        selectedAsset={selectedValue}
        onTreeSelectionChange={handleTreeSelectionChange}
        placeholder={'Выберите участок'}
        showClearOnHover
        showToggleButton
        toggleShowAllTooltip="Показать все участки системы"
        onToggleShowAll={toggleShowAllDepartments}
        showAll={showAllDepartments}
        toggleShowFilteredTooltip="Показать подходящие участки"
        emptyMessage={departmentPickerError ?? undefined}
        minWidth={400}
        deletedWarningTooltip="Участок удалён в справочнике"
        departmentDropdown={true}
        assetId={assetId}
      />
    </div>
  );
};
